/**
 * CSV upload modal for a campaign lookup.
 *
 * Two-step flow:
 *   Step 1 — pick file. Validate file size, parse with Papa, validate
 *            row/column caps and at-most-one-default before round-trip.
 *   Step 2 — preview + pick the key column + replace/merge mode →
 *            calls replace_lookup_rows RPC. Server re-validates the
 *            same caps; client validation is just a fast-fail.
 */

import { useRef, useState } from "react";
import Papa from "papaparse";
import {
  XMarkIcon,
  ArrowUpTrayIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import {
  LOOKUP_CAPS,
  type LookupRowUploadRow,
} from "../../../types/lookup.types";
import { replaceLookupRows } from "../../../services/lookups.service";
import {
  LOOKUP_DEFAULT_METADATA_COLUMN,
  parseLookupDefaultFlags,
} from "../../../utils/lookupCsv";

interface LookupCsvUploadModalProps {
  lookupId: string;
  defaultKeyColumn: string;
  /**
   * Columns that already exist in the lookup table (from
   * lookup.column_schema or derived from existing rows). Used to surface
   * a column-drift warning when the user picks Merge mode and the CSV's
   * headers don't match — without this nudge users hit the
   * "ghost lowercase column" footgun reported 2026-05-19.
   */
  existingColumns?: string[];
  onClose: () => void;
  onSuccess: () => void;
}

interface ParsedCsv {
  headers: string[];
  rows: Record<string, string>[];
  metadataDefaultFlags?: boolean[];
}

export default function LookupCsvUploadModal({
  lookupId,
  defaultKeyColumn,
  existingColumns,
  onClose,
  onSuccess,
}: LookupCsvUploadModalProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [parsed, setParsed] = useState<ParsedCsv | null>(null);
  const [keyColumn, setKeyColumn] = useState<string>(defaultKeyColumn);
  const [defaultColumn, setDefaultColumn] = useState<string>("");
  const [mode, setMode] = useState<"merge" | "replace">("merge");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleFile = (file: File) => {
    setError(null);
    if (file.size > LOOKUP_CAPS.MAX_FILE_SIZE_BYTES) {
      setError(
        `File is ${(file.size / 1024).toFixed(1)} KB — limit is ${LOOKUP_CAPS.MAX_FILE_SIZE_BYTES / 1024} KB. Trim the CSV or contact engineering.`,
      );
      return;
    }

    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        if (result.errors.length > 0) {
          setError(
            `CSV parse error on row ${result.errors[0].row}: ${result.errors[0].message}`,
          );
          return;
        }
        // Audit-followup #2 secondary: trim header labels AND re-key
        // each row's data to use trimmed names. Without this, a CSV
        // with " State " as a header would have its values living
        // under the untrimmed key but the rest of the modal looks them
        // up under "State".
        const originalHeaders = result.meta.fields ?? [];
        const headers = originalHeaders.map((h) => h.trim());
        if (headers.length === 0) {
          setError("CSV has no header row.");
          return;
        }
        // Case-INSENSITIVE dedupe: the bundle mapper and the DB schema trigger both match columns
        // case-insensitively, so "Section" and "section" (or "__is_default"/"__IS_DEFAULT") must be
        // rejected here rather than passing import as two columns and being silently dropped later.
        if (
          new Set(headers.map((h) => h.toLowerCase())).size !== headers.length
        ) {
          setError(
            "CSV contains duplicate column names (case-insensitive) after trimming.",
          );
          return;
        }
        const metadataHeader = headers.find(
          (h) => h.toLowerCase() === LOOKUP_DEFAULT_METADATA_COLUMN,
        );
        const contentHeaders = headers.filter((h) => h !== metadataHeader);
        if (contentHeaders.length === 0) {
          setError("CSV has no content columns.");
          return;
        }
        if (contentHeaders.length > LOOKUP_CAPS.MAX_COLUMNS) {
          setError(
            `CSV has ${contentHeaders.length} content columns — limit is ${LOOKUP_CAPS.MAX_COLUMNS}.`,
          );
          return;
        }
        const rows: Record<string, string>[] = result.data.map((row) => {
          const out: Record<string, string> = {};
          for (let i = 0; i < originalHeaders.length; i++) {
            const header = headers[i];
            if (header === metadataHeader) {
              continue;
            }
            out[header] = String(row[originalHeaders[i]] ?? "");
          }
          return out;
        });
        if (rows.length > LOOKUP_CAPS.MAX_ROWS) {
          setError(
            `CSV has ${rows.length} rows — limit is ${LOOKUP_CAPS.MAX_ROWS}.`,
          );
          return;
        }
        let metadataDefaultFlags: boolean[] | undefined;
        if (metadataHeader) {
          const originalMetadataHeader =
            originalHeaders[headers.indexOf(metadataHeader)];
          const parsedFlags = parseLookupDefaultFlags(
            result.data.map((row) => row[originalMetadataHeader]),
          );
          if (!parsedFlags.ok) {
            setError(
              `${LOOKUP_DEFAULT_METADATA_COLUMN} contains unsupported value "${parsedFlags.value}". Use true/false.`,
            );
            return;
          }
          metadataDefaultFlags = parsedFlags.flags;
        }
        setParsed({
          headers: contentHeaders,
          rows,
          metadataDefaultFlags,
        });
        // Default key column to the lookup's existing one if it appears
        // in the CSV; otherwise to the first header.
        if (contentHeaders.includes(defaultKeyColumn)) {
          setKeyColumn(defaultKeyColumn);
        } else {
          setKeyColumn(contentHeaders[0]);
        }
        setDefaultColumn(
          metadataDefaultFlags ? LOOKUP_DEFAULT_METADATA_COLUMN : "",
        );
      },
      error: (err) => {
        setError(`CSV parse failed: ${err.message}`);
      },
    });
  };

  const handleUpload = async () => {
    if (!parsed) {
      return;
    }
    setError(null);
    setSubmitting(true);

    try {
      // Validate at-most-one-default before round-tripping. If a default
      // column was picked, count the cells with a truthy CSV value.
      const defaultRowIndices: number[] = [];
      if (defaultColumn === LOOKUP_DEFAULT_METADATA_COLUMN) {
        parsed.metadataDefaultFlags?.forEach((isDefault, i) => {
          if (isDefault) {
            defaultRowIndices.push(i);
          }
        });
      } else if (defaultColumn) {
        for (let i = 0; i < parsed.rows.length; i++) {
          const r = parsed.rows[i];
          const parsedFlag = parseLookupDefaultFlags([r[defaultColumn]]);
          if (!parsedFlag.ok) {
            setError(
              `Default marker column contains unsupported value "${parsedFlag.value}". Use true/false.`,
            );
            setSubmitting(false);
            return;
          }
          if (parsedFlag.flags[0]) {
            defaultRowIndices.push(i);
          }
        }
        if (defaultRowIndices.length > 1) {
          setError(
            `${defaultRowIndices.length} rows are marked default. Only one default is allowed per lookup.`,
          );
          setSubmitting(false);
          return;
        }
      }

      // Validate the key column has a non-blank value on every row.
      const blankKeyRows: number[] = [];
      parsed.rows.forEach((r, i) => {
        if (!String(r[keyColumn] ?? "").trim()) {
          blankKeyRows.push(i + 2);
        } // +2 for 1-indexed + header offset
      });
      if (blankKeyRows.length > 0) {
        setError(
          `Rows ${blankKeyRows.slice(0, 5).join(", ")}${blankKeyRows.length > 5 ? "…" : ""} have a blank value for the key column "${keyColumn}".`,
        );
        setSubmitting(false);
        return;
      }

      // Build the RPC payload: each row is { data, is_default? }.
      const upload: LookupRowUploadRow[] = parsed.rows.map((r, i) => {
        const data: Record<string, unknown> = {};
        for (const h of parsed.headers) {
          data[h] = r[h] ?? "";
        }
        return {
          data,
          is_default: defaultColumn ? defaultRowIndices.includes(i) : false,
        };
      });

      await replaceLookupRows(
        lookupId,
        upload,
        keyColumn,
        mode,
        parsed.headers,
      );
      onSuccess();
    } catch (err) {
      setError(`Upload failed: ${(err as Error).message}`);
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold">Upload CSV</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {!parsed ? (
            <div>
              <p className="text-sm text-gray-600 mb-3">
                CSV must have a header row. Limits:{" "}
                {LOOKUP_CAPS.MAX_FILE_SIZE_BYTES / 1024} KB,{" "}
                {LOOKUP_CAPS.MAX_ROWS} rows, {LOOKUP_CAPS.MAX_COLUMNS} columns.
              </p>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleFile(file);
                  }
                }}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="w-full border-2 border-dashed border-gray-300 rounded-lg p-12 hover:border-blue-500 hover:bg-blue-50 transition-colors"
              >
                <ArrowUpTrayIcon className="mx-auto h-10 w-10 text-gray-400" />
                <span className="mt-2 block text-sm text-gray-700">
                  Click to choose a CSV file
                </span>
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-xs">
                <span className="font-semibold">{parsed.rows.length}</span> rows
                · <span className="font-semibold">{parsed.headers.length}</span>{" "}
                columns
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Which column is the key?
                </label>
                <select
                  value={keyColumn}
                  onChange={(e) => setKeyColumn(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {parsed.headers.map((h) => (
                    <option key={h} value={h}>
                      {h}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Contacts will be matched against this column at send time
                  (case-insensitive trim).
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Default-row marker column (optional)
                </label>
                <select
                  value={defaultColumn}
                  onChange={(e) => setDefaultColumn(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">(none — set default in the table)</option>
                  {parsed.metadataDefaultFlags && (
                    <option value={LOOKUP_DEFAULT_METADATA_COLUMN}>
                      {LOOKUP_DEFAULT_METADATA_COLUMN} (CSV metadata)
                    </option>
                  )}
                  {parsed.headers.map((h) => (
                    <option key={h} value={h}>
                      {h}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Most uploads should leave this as <em>(none)</em>. After
                  upload, click the star icon next to the row you want as the
                  default. Only pick a column here if your CSV already has a
                  dedicated true/false column flagging exactly one row as
                  default (cell value <code>yes</code> / <code>true</code> /{" "}
                  <code>1</code> / <code>y</code> / <code>default</code>). For
                  round-trip files, use the reserved metadata header{" "}
                  <code>{LOOKUP_DEFAULT_METADATA_COLUMN}</code>; it is consumed
                  by the importer and is never created as lookup content.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mode
                </label>
                <div className="space-y-2">
                  <label className="flex items-start gap-2 text-sm">
                    <input
                      type="radio"
                      name="mode"
                      value="merge"
                      checked={mode === "merge"}
                      onChange={() => setMode("merge")}
                      className="mt-0.5"
                    />
                    <div>
                      <span className="font-medium">Merge</span>
                      <span className="text-gray-500">
                        {" "}
                        — upsert by key. Existing rows are updated, new rows are
                        added, untouched rows are kept. Best when your CSV uses
                        the same column names as the table already has.
                      </span>
                    </div>
                  </label>
                  <label className="flex items-start gap-2 text-sm">
                    <input
                      type="radio"
                      name="mode"
                      value="replace"
                      checked={mode === "replace"}
                      onChange={() => setMode("replace")}
                      className="mt-0.5"
                    />
                    <div>
                      <span className="font-medium">Replace</span>
                      <span className="text-gray-500">
                        {" "}
                        — delete every existing row first, then load the CSV.
                        The CSV is the new full dataset. Use this if you've
                        changed column names or want to start clean.
                      </span>
                    </div>
                  </label>
                </div>

                {/* Column-drift warning in Merge mode. Surfaces the
                    "ghost lowercase column" footgun: re-uploading with
                    differently-cased headers in Merge mode leaves both the
                    old and the new column in the schema. Reported in prod
                    2026-05-19. */}
                {mode === "merge" &&
                  existingColumns &&
                  existingColumns.length > 0 &&
                  (() => {
                    const csvSet = new Set(parsed.headers);
                    const exSet = new Set(existingColumns);
                    const newCols = parsed.headers.filter((h) => !exSet.has(h));
                    const missingCols = existingColumns.filter(
                      (h) => !csvSet.has(h),
                    );
                    if (newCols.length === 0 && missingCols.length === 0) {
                      return null;
                    }
                    return (
                      <div className="mt-2 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                        <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="font-medium">
                            Merge will leave column drift in this table.
                          </div>
                          <div className="mt-1">
                            {newCols.length > 0 && (
                              <div>
                                CSV adds new columns:{" "}
                                <span className="font-mono">
                                  {newCols.join(", ")}
                                </span>
                              </div>
                            )}
                            {missingCols.length > 0 && (
                              <div>
                                CSV is missing existing columns:{" "}
                                <span className="font-mono">
                                  {missingCols.join(", ")}
                                </span>{" "}
                                (will stay in the table as empty cells)
                              </div>
                            )}
                            <div className="mt-1">
                              Pick <strong>Replace</strong> if you want the
                              table to match your CSV's columns exactly.
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
              </div>

              <div className="border-t border-gray-200 pt-3">
                <p className="text-xs text-gray-600 mb-2">
                  First 5 rows preview:
                </p>
                <div className="overflow-x-auto border border-gray-200 rounded">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        {parsed.headers.map((h) => (
                          <th
                            key={h}
                            className={`px-2 py-1 text-left font-mono ${h === keyColumn ? "bg-blue-100" : ""}`}
                          >
                            {h}
                            {h === keyColumn && (
                              <span className="ml-1 text-blue-600">★</span>
                            )}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {parsed.rows.slice(0, 5).map((row, i) => (
                        <tr key={i} className="border-t border-gray-100">
                          {parsed.headers.map((h) => (
                            <td
                              key={h}
                              className="px-2 py-1 truncate max-w-[150px]"
                            >
                              {row[h] ?? ""}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex justify-between gap-2 pt-3 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setParsed(null);
                    setError(null);
                  }}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Choose a different file
                </button>
                <button
                  type="button"
                  onClick={handleUpload}
                  disabled={submitting}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {submitting
                    ? "Uploading…"
                    : mode === "replace"
                      ? `Replace all rows`
                      : `Merge ${parsed.rows.length} rows`}
                </button>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 flex items-start gap-2">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
